import {
    MakeTitleInformationDTO,
    RvCollateralDTO,
    PostCollateralsAssetsDTO,
} from 'api/application/types.d'
import { getLosCondition } from 'api/utils/transformToLos'
import AssetDataSource from 'state/enums/AssetDataSource'

export interface RvVehicle {
    year: number
    modelId: number
    selectedVehicleId: string
    mileage?: string
    hasTitle: boolean
    noTitleOtherReason?: string
    noTitleReason?: string
    condition: string
    make: string
    makeId?: number
    name: string
    wholesale_price: string
    retail_price: string
    manufacturer_price: string
    oem?: string
    rv: number
    uuid: string
    model: string
    rv_type?: string
    asset_value?: string
    data_source?: string
}

export interface Rv {
    condition: string
    multiUnit: boolean
    isTrailer: boolean
    primary: RvVehicle
    secondary?: RvVehicle
    makeId: number
    vehicleType: string
}

function buildPostCollateralDtoToRvCollateral(
    condition: string,
    rv: RvVehicle,
): RvCollateralDTO {
    const collateralRv = {
        asset_type: 'recreational_vehicle',
        asset_subtype: '',
        asset_name: rv.name,
        data_source: rv.data_source ?? AssetDataSource.LOS,
        unique_external_id: rv.uuid,
        application: 0,
        is_auto_generated: false,
        asset_value: rv.asset_value || null,
        asset: {
            rv: rv.rv,
            rv_type: rv.rv_type || '',
            manufacturer_price: rv.manufacturer_price || '0',
            wholesale_price: rv.wholesale_price || '0',
            retail_price: rv.retail_price || '0',
            mileage: 0,
            oem: rv.oem,
            condition: getLosCondition(condition) || '',
            year: rv.year,
            make: rv.make,
            model: rv.model,
            name: rv.name,
        },
    }

    return collateralRv
}

function makeTitleInformation(rvVehicle: RvVehicle): MakeTitleInformationDTO {
    return {
        has_title: rvVehicle.noTitleReason !== '',
        no_title_reason:
            rvVehicle.noTitleReason !== ''
                ? rvVehicle.noTitleReason
                : undefined,
        no_title_other_reason:
            rvVehicle.noTitleOtherReason !== ''
                ? rvVehicle.noTitleOtherReason
                : undefined,
    }
}

function generateRvAssets(
    applicationId: number,
    condition: string,
    rvVehicle: RvVehicle,
) {
    const mileage = rvVehicle.mileage
        ? parseInt(rvVehicle.mileage, 10)
        : undefined

    const collateral = buildPostCollateralDtoToRvCollateral(
        condition,
        rvVehicle,
    )

    collateral.asset.mileage = mileage
    collateral.application = applicationId

    if (rvVehicle.condition === 'used') {
        collateral.asset.title_information = makeTitleInformation(rvVehicle)
    }

    return collateral
}

export async function generateRvCollaterals(
    applicationId: number,
    rv: Rv,
): Promise<PostCollateralsAssetsDTO<RvCollateralDTO>> {
    const collaterals = []

    collaterals.push(generateRvAssets(applicationId, rv.condition, rv.primary))

    if (rv.multiUnit && rv.secondary?.selectedVehicleId) {
        collaterals.push(
            generateRvAssets(applicationId, rv.condition, rv.secondary),
        )
    }

    return { collateral: collaterals }
}
